import {
  LANDING_HERO_COPY_MAX_LENGTHS,
  type LandingHeroCopy,
  type LandingHeroImageAdmin,
} from '@repo/api-contracts';
import { type FormEvent, type ReactElement, useState } from 'react';
import { Button } from '@/shared/ui';
import {
  buildLandingHeroCopyPlaceholders,
  LANDING_HERO_COPY_FIELDS,
  LANDING_HERO_COPY_LABELS,
  type LandingHeroCopyFieldName,
  type LandingHeroCopyFormErrors,
  type LandingHeroCopyFormValues,
  parseLandingHeroCopyForm,
  toLandingHeroCopyFormValues,
} from '../model/landing-hero-copy-form';
import { LandingHeroCopyField } from './landing-hero-copy-field';

interface LandingHeroCopyFormProps {
  readonly image: LandingHeroImageAdmin;
  /** 1부터 시작하는 노출 순번. */
  readonly order: number;
  readonly isBusy: boolean;
  readonly onSave: (copy: LandingHeroCopy) => void;
}

const isSameValues = (a: LandingHeroCopyFormValues, b: LandingHeroCopyFormValues): boolean =>
  LANDING_HERO_COPY_FIELDS.every((field) => a[field].trim() === b[field].trim());

/**
 * 배너 문구 직접 입력 — 작은 라벨·큰 문구·보조 문구·버튼 문구·버튼 링크.
 * 비운 칸은 연결 상품 정보로 대신한다(placeholder 가 무엇으로 대신하는지 보여 준다).
 * 저장이 끝나 목록이 다시 오면 카드가 key(updatedAt)로 이 폼을 새로 만든다 — 서버 값이 초기값이 된다.
 */
export const LandingHeroCopyForm = ({
  image,
  order,
  isBusy,
  onSave,
}: LandingHeroCopyFormProps): ReactElement => {
  const saved = toLandingHeroCopyFormValues(image.copy);
  const [values, setValues] = useState<LandingHeroCopyFormValues>(saved);
  const [errors, setErrors] = useState<LandingHeroCopyFormErrors>({});
  const placeholders = buildLandingHeroCopyPlaceholders(image.product);
  const isDirty = !isSameValues(values, saved);
  const hasSavedCopy = LANDING_HERO_COPY_FIELDS.some((field) => saved[field] !== '');

  const handleChange = (field: LandingHeroCopyFieldName, value: string): void => {
    setValues((current) => ({ ...current, [field]: value }));
    // 고친 칸의 오류만 지운다. 버튼 문구·링크는 서로 짝이라 함께 지운다.
    setErrors((current) => {
      const next = { ...current, [field]: undefined };
      if (field === 'ctaLabel' || field === 'ctaHref') {
        next.ctaLabel = undefined;
        next.ctaHref = undefined;
      }
      return next;
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const parsed = parseLandingHeroCopyForm(values);

    if (!parsed.ok) {
      setErrors(parsed.errors);
      return;
    }
    setErrors({});
    onSave(parsed.copy);
  };

  const handleClear = (): void => {
    setErrors({});
    onSave({ eyebrow: null, title: null, subtitle: null, ctaLabel: null, ctaHref: null });
  };

  return (
    <form
      aria-label={`${order}번 배너 문구`}
      noValidate
      onSubmit={handleSubmit}
      className="flex flex-col gap-2.5 rounded-xl bg-muted/50 p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">배너 문구</h3>
        <span className="text-[11px] text-muted-foreground">
          {hasSavedCopy ? '직접 입력한 문구 사용 중' : '비우면 연결 상품 정보'}
        </span>
      </div>

      {LANDING_HERO_COPY_FIELDS.map((field) => (
        <LandingHeroCopyField
          key={field}
          label={LANDING_HERO_COPY_LABELS[field]}
          accessibleName={`${order}번 배너 ${LANDING_HERO_COPY_LABELS[field]}`}
          value={values[field]}
          maxLength={LANDING_HERO_COPY_MAX_LENGTHS[field]}
          placeholder={placeholders[field]}
          error={errors[field]}
          disabled={isBusy}
          onChange={(value) => handleChange(field, value)}
        />
      ))}

      <div className="flex items-center justify-end gap-2 pt-1">
        {hasSavedCopy ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={isBusy}
            onClick={handleClear}
            aria-label={`${order}번 배너 문구 모두 지우기`}
          >
            모두 지우기
          </Button>
        ) : null}
        <Button
          type="submit"
          size="sm"
          disabled={isBusy || !isDirty}
          aria-label={`${order}번 배너 문구 저장`}
        >
          문구 저장
        </Button>
      </div>
    </form>
  );
};
